import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';
import { ASSETS } from '@/constants/assets';

const services = [
  { title: 'Diagnóstico de Sistemas Hidráulicos', desc: 'Avaliação técnica completa de sistemas hidráulicos em prensas, guilhotinas, dobradeiras e equipamentos de grande porte em Serra.' },
  { title: 'Substituição de Componentes', desc: 'Troca de componentes hidráulicos desgastados ou danificados com peças de qualidade equivalente para ambientes industriais intensos.' },
  { title: 'Troca de Mangueiras e Conexões', desc: 'Substituição de mangueiras hidráulicas e conexões em sistemas de alta pressão e alta frequência de operação.' },
  { title: 'Ajustes Operacionais', desc: 'Calibração e ajustes de parâmetros operacionais para otimização de performance em sistemas de siderurgia e metal mecânica.' },
  { title: 'Inspeções Hidráulicas Preventivas', desc: 'Inspeções preventivas para identificar condições e prever necessidades de intervenção em sistemas críticos.' },
  { title: 'Correção de Vazamentos', desc: 'Identificação e reparo de vazamentos em conexões, vedações, mangueiras e componentes hidráulicos em ambientes agressivos.' },
  { title: 'Montagem e Conexão de Sistemas', desc: 'Montagem e conexão de sistemas hidráulicos seguindo especificações técnicas e normas de segurança em grandes plantas.' },
  { title: 'Apoio Técnico Especializado', desc: 'Suporte técnico especializado em campo para diagnóstico, resolução e consultoria hidráulica em operações industriais.' },
];

const segments = [
  'Siderurgia',
  'Metal Mecânica',
  'Mineração',
  'Logística Industrial',
  'Grandes Plantas Industriais',
  'Transformação Metalúrgica',
];

const differentials = [
  'Atendimento rápido para Serra e polos industriais da Grande Vitória',
  'Experiência em sistemas hidráulicos de alta pressão e grande porte',
  'Equipe técnica especializada em diagnóstico e intervenção em campo',
  'Logística otimizada para reduzir tempo de parada',
  'Parceiros homologados para componentes e processos especiais',
  'Foco em segurança, confiabilidade e conformidade normativa',
];

const faqs = [
  { q: 'A Pilar atende diretamente em Serra?', a: 'Sim, a Pilar realiza atendimento diretamente na região de Serra, com equipe de campo própria para diagnóstico, intervenção e suporte técnico em sistemas hidráulicos industriais.' },
  { q: 'Quais tipos de sistemas hidráulicos são atendidos em Serra?', a: 'Sistemas hidráulicos de prensas, guilhotinas, dobradeiras, guindastes, esteiras, máquinas de mineração e equipamentos de grande porte em siderurgia e metal mecânica.' },
  { q: 'A Pilar trabalha em paradas de manutenção programadas?', a: 'Sim, a Pilar participa ativamente de paradas de manutenção programadas em grandes plantas industriais, com equipe escalada e planejamento de atividades conforme cronograma do cliente.' },
];

export default function SerraHidraulicaPage() {
  useSeo({
    title: 'Hidráulica Industrial em Serra | Serviços de Campo | Pilar',
    description: 'Serviços de hidráulica industrial em Serra e região. Manutenção, diagnóstico e recuperação de sistemas hidráulicos para siderurgia, metal mecânica, mineração e grandes plantas industriais da Grande Vitória.',
    keywords: 'hidráulica industrial Serra, manutenção hidráulica Serra, serviços hidráulicos Grande Vitória, sistemas hidráulicos siderurgia Serra, reparo hidráulico industrial ES, Grupo Pilar Serra',
    canonical: 'https://www.pilarmanutencao.com.br/hidraulica-industrial-serra',
    ogUrl: 'https://www.pilarmanutencao.com.br/hidraulica-industrial-serra',
    ogImage: ASSETS.oficina,
  });

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/LOCAL/SERRA/serra-hidraulica-hero.jpg"
              alt="Hidráulica industrial em Serra - Grupo Pilar"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-[2px] bg-gp-yellow"></div>
                <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Serra - Grande Vitória</span>
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-white uppercase leading-tight mb-6">
                <span className="text-gp-yellow">Hidráulica</span> Industrial em Serra
              </h1>
              <p className="text-gray-300 text-lg md:text-xl max-w-2xl leading-relaxed">
                Serviços de hidráulica industrial em campo para siderurgia, metal mecânica, mineração e grandes plantas industriais da região de Serra.
              </p>
            </div>
          </div>
        </section>

        {/* Introdução Local */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Atuação em Serra e Região</h2>
              </div>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Serra é um polo industrial de grande importância no Espírito Santo, com forte presença de siderurgia, metal mecânica e mineração. A Pilar oferece serviços de hidráulica industrial em campo, com equipe técnica especializada para atender sistemas hidráulicos de alta pressão e grande porte.
              </p>
              <p className="text-gray-400 leading-relaxed">
                Atuamos em diagnóstico, manutenção preventiva, corretiva e intervenções emergenciais em campo, com logística otimizada para a região da Grande Vitória.
              </p>
            </div>
          </div>
        </section>

        {/* Serviços */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Principais Serviços de Hidráulica em Serra</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="group bg-gp-graphite hover:bg-gp-graphite-lighter p-6 rounded-lg transition-all duration-300 hover:-translate-y-1 border border-transparent hover:border-gp-yellow/20"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center mb-4 group-hover:bg-gp-yellow transition-colors duration-300">
                    <span className="text-gp-yellow group-hover:text-gp-graphite font-display font-bold text-sm transition-colors duration-300">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="font-display text-base font-bold text-white uppercase mb-2 group-hover:text-gp-yellow transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{service.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Segmentos */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Segmentos Atendidos em Serra</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {segments.map((segment, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-lg px-5 py-4 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-2-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{segment}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Diferenciais */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Diferenciais da Pilar para Serra</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {differentials.map((diff, index) => (
                <div key={index} className="flex items-center gap-3 bg-gp-graphite border border-white/10 rounded-lg px-4 py-3 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{diff}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Perguntas Frequentes</h2>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-gp-graphite border border-white/10 rounded-lg p-6">
                    <h3 className="font-display text-base font-bold text-white uppercase mb-3">{faq.q}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Precisa de apoio em hidráulica industrial em Serra?
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Solicite uma avaliação técnica e conte com o Grupo Pilar para apoiar sua operação com serviços hidráulicos de campo na região da Grande Vitória.
                </p>
                <a href="https://wa.me/5527993092922" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap">
                  <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                    <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span>Solicitar Apoio Hidráulico</span>
                  <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Links Internos */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex flex-wrap items-center justify-center gap-6">
              <Link to="/hidraulica-industrial" className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider">
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Página Principal do Serviço
              </Link>
              <Link to="/contato" className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider">
                <i className="ri-mail-line w-5 h-5 flex items-center justify-center"></i>
                Contato
              </Link>
              <a href="https://wa.me/5527993092922" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider">
                <i className="ri-whatsapp-line w-5 h-5 flex items-center justify-center"></i>
                WhatsApp
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}